import React, { useEffect, useState } from 'react';
import { Box, FormControl, Select } from '@mui/material';
import { LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import NorthIndianChartRenderer, { NorthIndianPlanetLabel } from './NorthIndianChartRenderer';
import { EastIndianChartRenderer, SouthIndianChartRenderer } from './NorthIndianChart';
import { getActualHouseShort, getHouseShort, getPlanetAbbr, getSignAbbr } from '../../i18n/astrology';
import { isOuterPlanetKey, useUserPreferences } from '../../contexts/UserPreferencesContext';

const EXALT_SIGN: Record<string, number> = { SUN: 1, MOON: 2, MARS: 10, MERCURY: 6, JUPITER: 4, VENUS: 12, SATURN: 7, RAHU: 3, KETU: 9 };
const DEBIT_SIGN: Record<string, number> = { SUN: 7, MOON: 8, MARS: 4, MERCURY: 12, JUPITER: 10, VENUS: 6, SATURN: 1, RAHU: 9, KETU: 3 };
const COMBUST_ORB: Record<string, number> = { MOON: 12, MARS: 17, MERCURY: 14, JUPITER: 11, VENUS: 10, SATURN: 15 };

const getPlanetStatus = (key: string, planet: any, sunLongitude: number) => {
  const signNumber = planet?.sign?.number ?? 0;
  const speed = planet?.speed ?? 0;
  let status = '';

  if (EXALT_SIGN[key] === signNumber) status += 'E';
  if (DEBIT_SIGN[key] === signNumber) status += 'D';
  if (speed < 0 && key !== 'RAHU' && key !== 'KETU') status += 'R';

  const combustOrb = COMBUST_ORB[key];
  if (combustOrb && key !== 'SUN') {
    let arc = Math.abs((planet?.longitude ?? 0) - sunLongitude);
    if (arc > 180) arc = 360 - arc;
    if (arc < combustOrb) status += 'C';
  }

  return status;
};

const normalizeStatuses = (computedStatus: string, planetStatus: any) => {
  const statuses = new Set<string>();

  if (Array.isArray(planetStatus)) {
    planetStatus.forEach((status) => {
      const normalized = String(status || '').trim();
      if (normalized) statuses.add(normalized === 'Db' ? 'D' : normalized);
    });
  }

  computedStatus.split('').forEach((status) => {
    if (status) statuses.add(status);
  });

  return Array.from(statuses);
};

const getPlanetTextColor = (statuses: string[]) => {
  if (statuses.includes('E')) return '#1b7f2a';
  if (statuses.includes('D') || statuses.includes('Db')) return '#d32f2f';
  if (statuses.includes('V')) return '#7b1fa2';
  if (statuses.includes('R')) return '#e65100';
  if (statuses.includes('C')) return '#455a64';
  if (statuses.includes('A')) return '#00897b';
  return '#1b5e20';
};

const formatCuspDegrees = (longitude?: number) => {
  if (longitude === undefined) return '';
  const deg = Math.floor(longitude % 30);
  const min = Math.floor(((longitude % 30) - deg) * 60);
  return `${deg}°${min.toString().padStart(2, '0')}'`;
};

const normalizeDegrees = (longitude: number) => ((longitude % 360) + 360) % 360;

const isWithinArc = (longitude: number, start: number, end: number) => {
  const lon = normalizeDegrees(longitude);
  const arcStart = normalizeDegrees(start);
  const arcEnd = normalizeDegrees(end);

  if (arcEnd > arcStart) return lon >= arcStart && lon < arcEnd;
  return lon >= arcStart || lon < arcEnd;
};

const BhavaChalitChart: React.FC<{ chartData: any; size?: number; rotationHouse?: number; showHouseLabels?: boolean; showRotationControl?: boolean }> = ({
  chartData,
  size = 400,
  rotationHouse = 1,
  showHouseLabels = false,
  showRotationControl = false,
}) => {
  const { t } = useTranslation();
  const [selectedRotationHouse, setSelectedRotationHouse] = useState(rotationHouse);
  const { chartStyle, showOuterPlanets } = useUserPreferences();
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const isSignFixedStyle = chartStyle === 'south' || chartStyle === 'east';
  const ascSign = chartData?.houses?.ascendant?.sign?.number ?? 1;
  useEffect(() => setSelectedRotationHouse(rotationHouse), [rotationHouse]);
  const getActualHouseForDisplay = (displayHouse: number) => ((selectedRotationHouse + displayHouse - 2) % 12) + 1;

  const getCusp = (actualHouse: number) => (
    chartData?.bhavaChalit?.cusps?.[actualHouse] ?? chartData?.houses?.cusps?.[actualHouse]
  );

  const getSignOnCusp = (displayHouse: number) => {
    const actualHouse = getActualHouseForDisplay(displayHouse);
    const cusp = getCusp(actualHouse);
    if (cusp?.sign?.number) return cusp.sign.number;
    if (typeof cusp?.longitude === 'number') return Math.floor(cusp.longitude / 30) + 1;
    return displayHouse;
  };

  const getPlanetsForBhava = (displayHouse: number): NorthIndianPlanetLabel[] => {
    if (!chartData?.houses?.cusps || !chartData?.planets) return [];

    const actualHouse = getActualHouseForDisplay(displayHouse);
    const nextActualHouse = actualHouse === 12 ? 1 : actualHouse + 1;
    const currentCusp = chartData.houses.cusps[actualHouse]?.longitude;
    const nextCusp = chartData.houses.cusps[nextActualHouse]?.longitude;
    if (currentCusp === undefined || nextCusp === undefined) return [];

    const sunLongitude = chartData?.planets?.SUN?.longitude ?? chartData?.planets?.Sun?.longitude ?? 0;

    return Object.entries(chartData.planets)
      .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
      .filter(([, planet]: any) => {
        if (planet?.bhavaChalitHouse) return planet.bhavaChalitHouse === actualHouse;
        const longitude = planet.longitude;
        if (typeof longitude !== 'number') return false;
        return isWithinArc(longitude, currentCusp, nextCusp);
      })
      .map(([key, planet]: any) => {
        const name = String(planet?.name || key || '').toUpperCase();
        const statuses = normalizeStatuses(getPlanetStatus(name, planet, sunLongitude), planet?.status);

        return {
          abbr: getPlanetAbbr(t, name || key),
          degree: planet?.degree?.degrees ?? Math.floor((planet.longitude ?? 0) % 30),
          status: statuses.join(''),
          color: getPlanetTextColor(statuses),
        };
      });
  };

  const houses = Array.from({ length: 12 }, (_, index) => {
    const id = index + 1;
    const actualHouse = getActualHouseForDisplay(id);
    const cusp = getCusp(actualHouse);

    return {
      id,
      houseLabel: getHouseShort(t, id),
      actualHouseLabel: selectedRotationHouse !== 1 ? getActualHouseShort(t, actualHouse) : undefined,
      signNumber: getSignOnCusp(id),
      signLabel: getSignAbbr(t, getSignOnCusp(id)),
      secondaryLabel: formatCuspDegrees(cusp?.longitude),
      planets: getPlanetsForBhava(id),
    };
  });
  const signFixedHouses = Array.from({ length: 12 }, (_, index) => {
    const sign = index + 1;
    const bhavaHousesOnSign = houses.filter((item) => Number(item.signNumber) === sign);
    const houseLabel = bhavaHousesOnSign.map((item) => getHouseShort(t, item.id)).join(',');
    return {
      id: sign,
      houseLabel,
      signNumber: sign,
      signLabel: getSignAbbr(t, sign),
      lagnaMarker: sign === ascSign ? t('astrology.lagnaMarker') : undefined,
      secondaryLabel: bhavaHousesOnSign.map((item) => item.secondaryLabel).filter(Boolean).join(' / '),
      planets: bhavaHousesOnSign.flatMap((item) => item.planets),
    };
  });

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1">
        {t('astrology.bhavaChalitChart')}
        {!isSignFixedStyle ? ` • ${t('astrology.rotatedFromHouse', { number: selectedRotationHouse })}` : ''}
      </Typography>
      {showRotationControl && !isSignFixedStyle && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('astrology.rotateFromHouse')}</InputLabel>
            <Select
              value={selectedRotationHouse}
              label={t('astrology.rotateFromHouse')}
              onChange={(event) => setSelectedRotationHouse(Number(event.target.value))}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
                <MenuItem key={house} value={house}>{t('astrology.house', { number: house })}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {chartStyle === 'south' ? (
        <SouthIndianChartRenderer houses={signFixedHouses} size={size} ascSign={ascSign} />
      ) : chartStyle === 'east' ? (
        <EastIndianChartRenderer houses={signFixedHouses} size={size} ascSign={ascSign} />
      ) : (
        <NorthIndianChartRenderer houses={houses} size={size} showHouseLabels={showHouseLabels} style={{ marginTop: 8 }} />
      )}
    </Box>
  );
};

export default BhavaChalitChart;
